// React imports
import { useEffect } from "react";
import { Router, Route, Switch, useLocation } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "./components/ui/toaster";
import RootLayout from "./components/layout/RootLayout";

import HomePage from "./pages/HomePage";
import WorkPage from "./pages/WorkPage";
import PrivacyPage from "./pages/PrivacyPage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import LearnPage from "./pages/LearnPage";
import OnboardPage from "./pages/OnboardPage";
import PartnersPage from "./pages/PartnersPage";
import SocialPage from "./pages/SocialPage";
import BookingPage from "./pages/BookingPage";
import ContractorCRMPage from "./pages/ContractorCRMPage";
import YardsPage from "./pages/YardsPage";
import AiSalesPage from "./pages/AiSalesPage";
import InvoicePaymentPage from "./pages/InvoicePaymentPage";
import AIPresentationPage from "./pages/AIPresentationPage";
import PrivacyMyOrganicSoilPage from "./pages/PrivacyMyOrganicSoilPage";
import TermsMyOrganicSoilPage from "./pages/TermsMyOrganicSoilPage";
import SupportMyOrganicSoilPage from "./pages/SupportMyOrganicSoilPage";
import MorningBriefPage from "./pages/MorningBriefPage";
import HazeTVGamePlanPage from "./pages/HazeTVGamePlanPage";
import ClientFormsPage from "./pages/ClientFormsPage";
import TestalDoorDashAuthorizationPage from "./pages/TestalDoorDashAuthorizationPage";
import TgNotaryQuestionnairePage from "./pages/TgNotaryQuestionnairePage";
import { useScrollToTop } from "./hooks/useScrollToTop";

// Admin imports
import LoginPage from "./pages/admin/LoginPage";
import ForgotPasswordPage from "./pages/admin/ForgotPasswordPage";
import ResetPasswordPage from "./pages/admin/ResetPasswordPage";
import BillingPage from "./pages/admin/BillingPage";
import ConversationsPage from "./pages/admin/ConversationsPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminLayout from "./components/layout/AdminLayout";
import ReviewSurveyPage from "./pages/ReviewSurveyPage";
import OutreachDashboard from "./pages/admin/OutreachDashboard";
import RecordingSearchPage from "./pages/admin/RecordingSearchPage";
import MorePage from "./pages/admin/MorePage";
import DealsPage from "./pages/admin/DealsPage";
import DealDetailPage from "./pages/admin/DealDetailPage";
import TasksPage from "./pages/admin/TasksPage";
import ProductionReadinessPage from "./pages/admin/ProductionReadinessPage";
import DesktopMissionControlPage from "./pages/admin/DesktopMissionControlPage";
import ClientsPage from "./pages/admin/ClientsPage";
import ClientEditorPage from "./pages/admin/ClientEditorPage";
import TicketsPage from "./pages/admin/TicketsPage";

// Simple redirect component that doesn't cause infinite re-renders
function RedirectTo({ path }: { path: string }) {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(path, { replace: true });
  }, [navigate, path]);
  return null;
}


function App() {
  useScrollToTop();

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <RootLayout>
            <Switch>
              {/* Core Pages */}
              <Route path="/" component={HomePage} />
              <Route path="/services" component={ServicesPage} />
              <Route path="/work" component={WorkPage} />
              <Route path="/privacy" component={PrivacyPage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/learn" component={LearnPage} />
              <Route path="/contact" component={ContactPage} />
              <Route path="/partners" component={PartnersPage} />
              <Route path="/client-onboarding">
                <RedirectTo path="/onboard" />
              </Route>
              <Route path="/onboard" component={OnboardPage} />
              <Route path="/start" component={OnboardPage} />
              <Route path="/rodolfo" component={SocialPage} />
              <Route path="/book" component={BookingPage} />
              <Route path="/contractors" component={ContractorCRMPage} />
              <Route path="/yards" component={YardsPage} />
              <Route path="/ai-sales" component={AiSalesPage} />
              <Route path="/scorecard">
                <RedirectTo path="/ai-sales" />
              </Route>
              <Route path="/review" component={ReviewSurveyPage} />
              <Route path="/pay/:invoiceNumber" component={InvoicePaymentPage} />
              <Route path="/ai-presentation" component={AIPresentationPage} />
              <Route path="/morning-brief" component={MorningBriefPage} />
              <Route path="/brief" component={MorningBriefPage} />
              <Route path="/haze-tv-game-plan" component={HazeTVGamePlanPage} />
              <Route path="/client-forms" component={ClientFormsPage} />
              <Route path="/client-forms/testal-doordash" component={TestalDoorDashAuthorizationPage} />
              <Route path="/client-forms/tg-notary-questionnaire" component={TgNotaryQuestionnairePage} />

              {/* Old public URLs */}
              <Route path="/pricing">
                <RedirectTo path="/services" />
              </Route>
              <Route path="/get-started">
                <RedirectTo path="/book" />
              </Route>
              <Route path="/services/pre-assessment">
                <RedirectTo path="/book" />
              </Route>
              <Route path="/services/ai-efficiency-assessment">
                <RedirectTo path="/services" />
              </Route>
              <Route path="/services/efficiency-audit">
                <RedirectTo path="/services" />
              </Route>
              <Route path="/services/ai-assistants">
                <RedirectTo path="/services" />
              </Route>
              <Route path="/services/ai-assistants/personal-assistant-tutorial">
                <RedirectTo path="/services" />
              </Route>

              {/* SSW Sales Portal app — legal + support pages (App Store compliance) */}
              <Route path="/privacy/myorganicsoil" component={PrivacyMyOrganicSoilPage} />
              <Route path="/terms/myorganicsoil" component={TermsMyOrganicSoilPage} />
              <Route path="/support/myorganicsoil" component={SupportMyOrganicSoilPage} />

              {/* Admin Routes */}
              <Route path="/admin/login" component={LoginPage} />
              <Route path="/admin/forgot-password" component={ForgotPasswordPage} />
              <Route path="/admin/reset-password" component={ResetPasswordPage} />

              {/* Desktop admin entry */}
              <Route path="/admin">
                <RedirectTo path="/admin/mission-control" />
              </Route>

              {/* Redirect old routes to Mission Control */}
              <Route path="/admin/dashboard">
                <RedirectTo path="/admin/mission-control" />
              </Route>

              <Route path="/admin/mission-control">
                <ProtectedRoute>
                  <AdminLayout>
                    <DesktopMissionControlPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Conversations */}
              <Route path="/admin/conversations">
                <ProtectedRoute>
                  <AdminLayout>
                    <ConversationsPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Client records */}
              <Route path="/admin/clients/:id">
                <ProtectedRoute>
                  <AdminLayout>
                    <ClientEditorPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              <Route path="/admin/clients">
                <ProtectedRoute>
                  <AdminLayout>
                    <ClientsPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Outreach CRM */}
              <Route path="/admin/outreach">
                <ProtectedRoute>
                  <AdminLayout>
                    <OutreachDashboard />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Deals Pipeline */}
              <Route path="/admin/deals/:id">
                <ProtectedRoute>
                  <AdminLayout>
                    <DealDetailPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              <Route path="/admin/deals">
                <ProtectedRoute>
                  <AdminLayout>
                    <DealsPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Bug tracker */}
              <Route path="/admin/tickets">
                <ProtectedRoute>
                  <AdminLayout>
                    <TicketsPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Recording Intelligence */}
              <Route path="/admin/search">
                <ProtectedRoute>
                  <AdminLayout>
                    <RecordingSearchPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Tasks — Task Management & Agent Progress */}
              <Route path="/admin/tasks">
                <ProtectedRoute>
                  <AdminLayout>
                    <TasksPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Production Readiness */}
              <Route path="/admin/production-readiness">
                <ProtectedRoute>
                  <AdminLayout>
                    <ProductionReadinessPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* More — Speaker Profiles & Intelligence */}
              <Route path="/admin/more">
                <ProtectedRoute>
                  <AdminLayout>
                    <MorePage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Financial — kept as-is */}
              <Route path="/admin/billing">
                <ProtectedRoute>
                  <AdminLayout>
                    <BillingPage />
                  </AdminLayout>
                </ProtectedRoute>
              </Route>

              {/* Unknown public URLs */}
              <Route>
                <RedirectTo path="/" />
              </Route>
            </Switch>
            <Toaster />
          </RootLayout>
        </Router>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
